import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type AnalyticsAttributes, fireOperationalAnalytics } from '@atlassian/analytics-bridge';

import { logException } from '../sentry';

import { type ReliabilityEvent, studioReliabilityTracker } from './studio-reliability-tracker';

const ACTION_BY_TYPE: Record<ReliabilityEvent['type'], string> = {
	start: 'taskStart',
	succeed: 'taskSuccess',
	fail: 'taskFail',
	abort: 'taskAbort',
};

const buildAnalyticsAttributes = (event: ReliabilityEvent): AnalyticsAttributes => {
	const base: AnalyticsAttributes = {
		task: event.name,
	};

	if (event.id !== undefined) {
		base.taskId = event.id;
	}

	if (event.touchpointId !== undefined) {
		base.touchpointId = event.touchpointId;
	}

	if (event.type !== 'start') {
		base.duration = event.duration;
	}

	if (event.type === 'fail' && event.traceId !== undefined) {
		base.atlTraceId = event.traceId;
	}

	if (event.type === 'abort' && event.reason !== undefined) {
		base.reason = event.reason;
	}

	if (event.attributes) {
		Object.entries(event.attributes).forEach(([key, value]) => {
			if (value !== undefined) {
				base[key] = value;
			}
		});
	}

	return base;
};

const reportFailToSentry = (event: Extract<ReliabilityEvent, { type: 'fail' }>): void => {
	logException(event.error, {
		loggerName: `StudioReliability-${event.name}`,
		touchpointId: event.touchpointId,
		task: event.name,
		taskId: event.id,
		atlTraceId: event.traceId,
		duration: event.duration,
		reliabilityEvent: 'fail',
		...event.attributes,
	});
};

const reportAbortToSentry = (event: Extract<ReliabilityEvent, { type: 'abort' }>): void => {
	logException(new Error(event.reason ?? `Reliability tracker aborted: ${event.name}`), {
		loggerName: `StudioReliability-${event.name}`,
		touchpointId: event.touchpointId,
		task: event.name,
		taskId: event.id,
		duration: event.duration,
		reason: event.reason,
		reliabilityEvent: 'abort',
		...event.attributes,
	});
};

export const ReliabilityTrackerSubscribers = (): null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		return studioReliabilityTracker.subscribe((event) => {
			const action = ACTION_BY_TYPE[event.type];
			const analyticsEvent = createAnalyticsEvent({
				action,
				actionSubject: 'ui',
			});
			fireOperationalAnalytics(analyticsEvent, `ui ${action}`, buildAnalyticsAttributes(event));

			if (event.type === 'fail') {
				reportFailToSentry(event);
			} else if (event.type === 'abort') {
				reportAbortToSentry(event);
			}
		});
	}, [createAnalyticsEvent]);

	return null;
};
